class AnimatedBackground {
    constructor() {
        this.canvas = null;
        this.ctx = null;
        this.animationId = null;
        this.time = 0;
        this.isDark = false;
        
        // Light mode colors (matching Next.js)
        this.lightColors = [
            [255, 107, 53],   // #ff6b35
            [247, 147, 30],   // #f7931e  
            [255, 144, 104],  // #ff9068
            [255, 167, 38],   // #ffa726
            [255, 196, 117]   // #ffc475
        ];
        
        // Dark mode colors (matching Next.js)
        this.darkColors = [
            [26, 26, 46],     // #1a1a2e
            [22, 33, 62],     // #16213e
            [15, 52, 96],     // #0f3460
            [83, 52, 131],    // #533483
            [45, 45, 75]      // #2d2d4b
        ];
    }

    init() {
        this.canvas = document.getElementById('noise-canvas');
        if (!this.canvas) return;
        
        this.ctx = this.canvas.getContext('2d');
        this.setupCanvas();
        this.checkTheme();
        this.setupThemeObserver();
        this.animate();
    }

    setupCanvas() {
        const resizeCanvas = () => {
            const dpr = window.devicePixelRatio || 1;
            const rect = this.canvas.getBoundingClientRect();
            
            this.canvas.width = rect.width * dpr;
            this.canvas.height = rect.height * dpr;
            
            this.ctx.scale(dpr, dpr);
            this.canvas.style.width = rect.width + 'px';
            this.canvas.style.height = rect.height + 'px';
        };
        
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
    }

    checkTheme() {
        this.isDark = document.documentElement.classList.contains('dark');
    }

    setupThemeObserver() {
        const observer = new MutationObserver(() => {
            this.checkTheme();
        });
        
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    // Simple noise function (Perlin-like)
    noise(x, y, z = 0) {
        // Simple pseudo-random noise
        const n = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453;
        return (n - Math.floor(n)) * 2 - 1; // Range: -1 to 1
    }

    // Smooth interpolation
    smoothstep(edge0, edge1, x) {
        const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
        return t * t * (3 - 2 * t);
    }

    // Generate color based on noise
    getColorAt(x, y) {
        const colors = this.isDark ? this.darkColors : this.lightColors;
        const scale = 0.01;
        const timeScale = 0.001;
        
        // Multi-octave noise for more interesting patterns
        let noise1 = this.noise(x * scale, y * scale, this.time * timeScale);
        let noise2 = this.noise(x * scale * 2, y * scale * 2, this.time * timeScale * 1.5);
        let noise3 = this.noise(x * scale * 4, y * scale * 4, this.time * timeScale * 0.5);
        
        let combined = (noise1 * 0.6 + noise2 * 0.3 + noise3 * 0.1);
        combined = (combined + 1) * 0.5; // Normalize to 0-1
        
        // Map noise to color indices
        const colorIndex = combined * (colors.length - 1);
        const colorIndexFloor = Math.floor(colorIndex);
        const colorIndexCeil = Math.min(colorIndexFloor + 1, colors.length - 1);
        const colorMix = colorIndex - colorIndexFloor;
        
        const color1 = colors[colorIndexFloor];
        const color2 = colors[colorIndexCeil];
        
        // Interpolate between colors
        const r = Math.round(color1[0] * (1 - colorMix) + color2[0] * colorMix);
        const g = Math.round(color1[1] * (1 - colorMix) + color2[1] * colorMix);
        const b = Math.round(color1[2] * (1 - colorMix) + color2[2] * colorMix);
        
        return `rgb(${r}, ${g}, ${b})`;
    }

    drawNoise() {
        const width = this.canvas.width;
        const height = this.canvas.height;
        const imageData = this.ctx.createImageData(width, height);
        const data = imageData.data;
        
        // Sample size for performance (render at lower resolution)
        const step = 4;
        
        for (let x = 0; x < width; x += step) {
            for (let y = 0; y < height; y += step) {
                const color = this.getColorAt(x, y);
                const rgb = this.parseRGB(color);
                
                // Fill a block instead of single pixel for performance
                for (let dx = 0; dx < step && x + dx < width; dx++) {
                    for (let dy = 0; dy < step && y + dy < height; dy++) {
                        const index = ((y + dy) * width + (x + dx)) * 4;
                        data[index] = rgb.r;
                        data[index + 1] = rgb.g;
                        data[index + 2] = rgb.b;
                        data[index + 3] = 255; // Alpha
                    }
                }
            }
        }
        
        this.ctx.putImageData(imageData, 0, 0);
    }

    parseRGB(rgbString) {
        const match = rgbString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
        return {
            r: parseInt(match[1]),
            g: parseInt(match[2]),
            b: parseInt(match[3])
        };
    }

    animate() {
        this.time += 1;
        this.drawNoise();
        
        this.animationId = requestAnimationFrame(() => this.animate());
    }

    destroy() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    if (document.getElementById('noise-canvas')) {
        window.potredAnimatedBackground = new AnimatedBackground();
        window.potredAnimatedBackground.init();
    }
});

// Cleanup on page unload
window.addEventListener('beforeunload', function() {
    if (window.potredAnimatedBackground) {
        window.potredAnimatedBackground.destroy();
    }
});